import React, { useState } from 'react';
import { useBlockedUsers, useUnblockUser } from '@/hooks/api';
import { LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { useUI, useAppStore } from '@/store';
import type { BlockedUser } from '@/types';
import ProfileDetailScreen from './ProfileDetailScreen';

const BlockedUsersScreen: React.FC = () => {
  const { setActiveScreen } = useUI();
  const { ui, setViewingProfile } = useAppStore();
  const { data, isLoading } = useBlockedUsers();
  const unblockUser = useUnblockUser();
  const [unblockingUserId, setUnblockingUserId] = useState<number | null>(null);

  const handleBack = () => {
    setActiveScreen('settings');
    TelegramUtils.haptic.light();
  };

  const handleUnblock = async (user: BlockedUser) => {
    const confirmed = await TelegramUtils.showConfirm(
      `Разблокировать ${user.first_name}?`
    );

    if (!confirmed) return;

    setUnblockingUserId(user.user_id);
    unblockUser.mutate(String(user.user_id), {
      onSettled: () => {
        setUnblockingUserId(null);
      }
    });
  };

  const handleViewProfile = (userId: number) => {
    setViewingProfile(String(userId));
    TelegramUtils.haptic.light();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Сегодня';
    if (diffDays === 1) return 'Вчера';
    if (diffDays < 7) return `${diffDays} дней назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (isLoading) {
    return <LoadingScreen message="Загрузка..." />;
  }

  // Show profile detail if user clicked on a blocked user
  if (ui.viewingProfileId) {
    return (
      <ProfileDetailScreen
        userId={ui.viewingProfileId}
        onBack={() => setViewingProfile(null)}
        onSwipe={() => {
          // Just close profile, no swipe action for blocked users
          setViewingProfile(null);
        }}
      />
    );
  }

  const blockedUsers: BlockedUser[] = data?.blocked_users || [];
  const hasBlockedUsers = blockedUsers.length > 0;

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 sticky top-0 bg-tg-bg/95 backdrop-blur-xl z-10">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h1 className="text-xl font-bold text-white absolute left-1/2 transform -translate-x-1/2">
          Заблокированные
        </h1>
        
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Content */}
      <div className="p-4">
        {!hasBlockedUsers ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Нет заблокированных
            </h2>
            <p className="text-neutral-400 text-sm max-w-xs">
              Пользователи, которых вы заблокируете, появятся здесь
            </p>
          </div>
        ) : (
          // List of blocked users
          <div className="space-y-3">
            <div className="text-sm text-neutral-500 mb-4">
              Заблокировано: {blockedUsers.length}
            </div>

            {blockedUsers.map((user) => (
              <button
                key={user.user_id}
                onClick={() => handleViewProfile(user.user_id)}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  {/* Photo */}
                  {user.main_photo && (
                    <img
                      src={user.main_photo}
                      alt={user.first_name}
                      className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                    />
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-lg">
                      {user.first_name} {user.age}
                    </h3>
                    <p className="text-neutral-500 text-sm">
                      Заблокирован {formatDate(user.blocked_at)}
                    </p>
                  </div>

                  {/* Unblock button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnblock(user);
                    }}
                    disabled={unblockingUserId === user.user_id}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-sm font-medium hover:bg-white/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    {unblockingUserId === user.user_id ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Разблокировать'
                    )}
                  </button>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      {hasBlockedUsers && (
        <div className="p-4">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">ℹ️</div>
              <div>
                <p className="text-sm text-white/70 leading-relaxed">
                  Заблокированные пользователи не смогут видеть ваш профиль в ленте и отправлять вам сообщения.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockedUsersScreen;

